import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Copy, Printer, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SummaryPage() {
  const [userId, setUserId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUserId = sessionStorage.getItem('serenity_user_id');
    if (!storedUserId) {
      toast.error('Please log in first');
      navigate('/');
      return;
    }
    setUserId(storedUserId);
    fetchSummary(storedUserId);
  }, [navigate]);

  const fetchSummary = async (uid) => {
    try {
      const response = await axios.get(`${API}/serenity/summary/${uid}`);
      setSummary(response.data.summary);
      setLoading(false);
    } catch (error) {
      console.error('Summary fetch error:', error);
      if (error.response?.status === 404) {
        toast.error('Pathway not generated yet. Complete the discovery questions first.');
      } else {
        toast.error('Failed to load pathway');
      }
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (summary) {
      navigator.clipboard.writeText(summary);
      setCopied(true);
      toast.success('Pathway copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-serenity-accent-blue border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-serenity-secondary">Loading your pathway...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-16 h-16 bg-serenity-error/20 rounded-full flex items-center justify-center mx-auto">
            <span className="text-3xl">💭</span>
          </div>
          <div>
            <h2 className="font-playfair text-2xl text-serenity-primary mb-2">
              No Pathway Yet
            </h2>
            <p className="text-serenity-secondary">
              Complete the discovery journey to generate your personalized Serenity Pathway.
            </p>
          </div>
          <Button
            onClick={() => navigate('/chat')}
            className="bg-serenity-primary-blue text-white hover:bg-[#1a3fb3] rounded-full px-8 py-3 font-semibold"
          >
            Continue Journey
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="bg-white border-b border-serenity-border sticky top-0 z-10 print:hidden shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-serenity-primary-blue">
                <img 
                  src="https://customer-assets.emergentagent.com/job_mentor-chat-3/artifacts/5i6s80b4_Gemini_Generated_Image_13pb5e13pb5e13pb-2.png" 
                  alt="Serenity Avatar"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="font-playfair text-2xl text-serenity-primary-blue font-semibold">
                Your Serenity Pathway
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleCopy}
                variant="outline"
                size="sm"
                className="border-serenity-border text-serenity-primary-blue hover:bg-serenity-light-blue"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4 mr-2" />
                ) : (
                  <Copy className="w-4 h-4 mr-2" />
                )}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button
                onClick={handlePrint}
                variant="outline"
                size="sm"
                className="border-serenity-border text-serenity-primary-blue hover:bg-serenity-light-blue"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button
                onClick={() => navigate('/chat')}
                variant="ghost"
                size="sm"
                className="text-serenity-secondary hover:text-serenity-primary-blue hover:bg-serenity-light-blue"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-5xl mx-auto px-6 py-12"
      >
        <div 
          data-testid="summary-content"
          className="bg-serenity-light-blue border border-serenity-border shadow-lg rounded-3xl p-12"
        >
          <div className="markdown-content prose prose-lg max-w-none">
            <ReactMarkdown>{summary}</ReactMarkdown>
          </div>
        </div>

        <div className="mt-8 text-center print:hidden">
          <Button
            onClick={() => navigate('/chat')}
            className="bg-serenity-primary-blue text-white hover:bg-[#1a3fb3] rounded-full px-8 py-3 shadow-md hover:shadow-lg font-semibold"
          >
            Continue Conversation
          </Button>
        </div>
      </motion.div>
    </div>
  );
}